import { ReactNode, RefObject, FC } from "react";

import { Box, Grid } from "@mui/material";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export interface LiveEditorStepLayoutProps {
  slots: {
    content: React.ReactNode;
  };
  title?: string;
  titleTooltip?: string;
  description?: string | ReactNode;
  "data-test"?: string;
  formContainerRef?: RefObject<HTMLDivElement | null>;
}

export const LiveEditorStepLayout: FC<LiveEditorStepLayoutProps> = ({
  slots,
  title,
  titleTooltip,
  description,
  "data-test": dataTest,
  formContainerRef,
}) => {
  const { isSmallScreen } = useMediaQuery();

  return (
    <Grid
      container
      direction="column"
      sx={{
        height: "100%",
        maxHeight: "100%",
      }}
      data-test={dataTest || "step-container"}
    >
      <Grid
        item
        xs
        sx={{
          flex: 1,
          overflowY: "auto",
          paddingTop: { xs: 5, md: 6 },
          paddingX: { xs: 3, md: 6 },
          paddingBottom: 6,
          width: "100%", // Required to prevent content from overflowing the container. Eg Froala editor
        }}
        data-test="step-content"
        ref={formContainerRef as RefObject<HTMLDivElement> | undefined}
      >
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1, paddingBottom: 2 }}>
          {/* Title with optional tooltip */}
          {title && (
            <DashboardTypography
              variant={isSmallScreen ? "h5" : "h4"}
              data-test="step-title"
              sx={{
                display: "inline",
                "& svg": {
                  marginLeft: 1,
                  verticalAlign: "middle",
                  display: "inline-block",
                },
              }}
            >
              {title}
              {/* Info icon with tooltip */}
              {titleTooltip && (
                <DashboardInformationTooltip title={titleTooltip} slotProps={{ icon: { size: "large" } }} />
              )}
            </DashboardTypography>
          )}

          {/* Optional description text */}
          {description && <DashboardTypography variant="body2">{description}</DashboardTypography>}
        </Box>

        {slots.content}
      </Grid>
    </Grid>
  );
};
